/**
 * Hook para gerenciar dados da Waiting List V2.
 * Carrega lista de pacientes e expõe ações (CRUD).
 *
 * resourceId: filtro por profissional feito no servidor (entradas dele e as de
 * "Qualquer profissional"), para não depender da primeira página carregada.
 *
 * lastOffers: última oferta de horário de cada entrada ({ [waitingListId]: oferta }),
 * com quem ofereceu e quando (pela lista de espera ou pela agenda).
 */
import { useState, useEffect, useCallback, useMemo } from 'react';
import { DataProviderInstance } from '../../../api/DataProvider';

export function useWaitingListData({ resourceId = '' } = {}) {
  const {
    GetWaitingList,
    SetWaitingList,
    UpdateWaitingList,
    DeleteWaitingList,
    SetWaitingListOffer,
    GetWaitingListOffers,
    SetAppointment,
    GetPatientById,
    GetProcedureById,
    GetResourceById,
    CompanyConfig,
    UserData,
  } = DataProviderInstance();

  const stableCompanyConfig = useMemo(() => CompanyConfig, [CompanyConfig?.Id]);

  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });
  const [lastOffers, setLastOffers] = useState({});

  const loadLastOffers = useCallback(() => {
    GetWaitingListOffers({ latest: 1 }, (response) => {
      if (!response?.success) return;
      const byItem = {};
      (response.data || []).forEach((offer) => { byItem[offer.waitingListId] = offer; });
      setLastOffers(byItem);
    });
  }, [GetWaitingListOffers]);

  const loadData = useCallback((params = {}) => {
    if (!stableCompanyConfig?.Id) return;

    setLoading(true);
    setError(null);

    const requestParams = {
      companyId: stableCompanyConfig.Id,
      page: params.page || 1,
      limit: params.limit || 100,
      includeAvailability: true,
      daysLimit: params.daysLimit || 30,
      resourceId: resourceId || undefined,
      ...params,
    };

    GetWaitingList(requestParams, (response) => {
      if (response.error) {
        setError(response.error);
        setPatients([]);
      } else {
        setPatients(response.waitingList || []);
        setPagination({
          page: response.pagination?.page || 1,
          pages: response.pagination?.pages || 1,
          total: response.pagination?.total || 0,
        });
      }
      setLoading(false);
    });
    loadLastOffers();
  }, [stableCompanyConfig, GetWaitingList, resourceId, loadLastOffers]);

  // Carrega dados iniciais (não depende de loadData para evitar loop de re-renders
  // causado por referências instáveis de GetWaitingList no DataProvider)
  useEffect(() => {
    if (stableCompanyConfig?.Id) {
      loadData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stableCompanyConfig?.Id, resourceId]);

  const addPatient = useCallback((data, callback) => {
    SetWaitingList(data, (response) => {
      if (response.success) loadData();
      callback?.(response);
    });
  }, [SetWaitingList, loadData]);

  const updatePatient = useCallback((data, callback) => {
    UpdateWaitingList({ ...data, updatedBy: UserData?.id }, (response) => {
      if (response.success) loadData();
      callback?.(response);
    });
  }, [UpdateWaitingList, UserData, loadData]);

  const deletePatient = useCallback((id, callback) => {
    DeleteWaitingList(id, (response) => {
      if (response.success) loadData();
      callback?.(response);
    });
  }, [DeleteWaitingList, loadData]);

  // Register an offer (slot offered to patient via WhatsApp)
  const registerOffer = useCallback((data, callback) => {
    SetWaitingListOffer(data, (response) => {
      if (response?.success) loadLastOffers();
      callback?.(response);
    });
  }, [SetWaitingListOffer, loadLastOffers]);

  // Create appointment from waiting list
  const createAppointment = useCallback((data, callback) => {
    SetAppointment(data, (response) => {
      if (response?.status === 'SUCCESS') {
        // Mark waiting list item as SCHEDULED
        if (data.waitingListItemId) {
          updatePatient({ id: data.waitingListItemId, status: 'SCHEDULED' });
        }
      }
      callback?.(response);
    });
  }, [SetAppointment, updatePatient]);

  // Com "Só os meus pacientes", a entrada de "Qualquer profissional" pode ser de alguém
  // fora da lista de pacientes carregada: vale o nome e o telefone que vêm na entrada
  const patientSnapshots = useMemo(() => {
    const map = new Map();
    patients.forEach((item) => {
      if (item?.patient) map.set(String(item.patientId), item.patient);
    });
    return map;
  }, [patients]);
  const getPatientById = useCallback(
    (id) => GetPatientById(id) || patientSnapshots.get(String(id)),
    [GetPatientById, patientSnapshots]
  );

  return {
    patients,
    loading,
    error,
    pagination,
    loadData,
    addPatient,
    updatePatient,
    deletePatient,
    registerOffer,
    lastOffers,
    createAppointment,
    companyConfig: stableCompanyConfig,
    userData: UserData,
    getPatientById,
    getProcedureById: GetProcedureById,
    getResourceById: GetResourceById,
  };
}
